import React, { useCallback } from 'react';
import api from '../../../../Api';
import { ShareTargetType } from './ShareTargetType';
import { Action } from './useShareTargets';

export const NominationAction = {
	NOMINATIONS_SUBMITTED: 'NOMINATIONS_SUBMITTED',
};

export default function useNominationTargets(sku) {
	const [isLoading, setIsLoading] = React.useState(true);
	const [nominationOptions, setNominationOptions] = React.useState([]);

	const [alreadyNominatedIds, setAlreadyNominatedIds] = React.useState([]);
	const [nominationTargetIds, setNominationTargetIds] = React.useState([]);

	const fetchNominatedCallback = useCallback(async () => {
		const url = `/api/me/reviews/products/${sku}/shares-inclusions/nominations`;
		const nominated = await api.get(url);

		if (Array.isArray(nominated)) {
			const nominatedIds = nominated.map(n => n.submissionListId);
			setNominationTargetIds(nominatedIds);
			setAlreadyNominatedIds(nominatedIds);
		}
	}, [sku]);

	React.useEffect(() => {
		let request;

		const fetchSubmittableNominationSubmissionLists = async () => {
			const url = `/api/affiliations/submissionLists/submittable/${sku}`;
			request = api.cancelableGet(url);

			const nominationOptions = await request.promise;
			setNominationOptions(nominationOptions);
		};

		const initialize = async () => {
			await fetchNominatedCallback();

			await fetchSubmittableNominationSubmissionLists();

			setIsLoading(false);
		};

		initialize();

		return () => {
			api.revoke(request);
		};
	}, [sku, fetchNominatedCallback]);

	const isActiveNomination = shareTarget => {
		switch (shareTarget.type) {
			case ShareTargetType.nomination:
				return nominationTargetIds.includes(shareTarget.id);
			default:
				return false;
		}
	};

	const handleNominationsSubmitted = () => {
		setAlreadyNominatedIds(alreadyNominatedIds => {
			const idsToAdd = [];

			nominationTargetIds.forEach(targetId => {
				if (!alreadyNominatedIds.includes(targetId)) {
					idsToAdd.push(targetId);
				}
			});

			return [...alreadyNominatedIds, ...idsToAdd];
		});
	};

	const reduceState = action => {
		const { type, payload: p } = action;
		switch (type) {
			case Action.ADD_SHARE_TARGET:
				setNominationTargetIds(ids => [...ids, p.shareTarget.id]);
				break;
			case Action.REMOVE_SHARE_TARGET:
				setNominationTargetIds(ids =>
					ids.filter(id => id !== p.shareTarget.id),
				);
				break;
			case NominationAction.NOMINATIONS_SUBMITTED:
				handleNominationsSubmitted();
				break;
			case Action.CLEAR_SHARE_TARGETS:
				setNominationTargetIds([]);
				break;
			default:
				break;
		}
	};

	return {
		isLoading: () => isLoading,
		isActive: isActiveNomination,
		getOptions: () => nominationOptions,
		update: reduceState,
		isNominationSubmitted: id => alreadyNominatedIds.includes(id),
		refresh: () => fetchNominatedCallback(),
		getAlreadySubmittedCount: () => alreadyNominatedIds.length,
		getSelectedNominationAffiliationIds: () =>
			nominationTargetIds.filter(id => !alreadyNominatedIds.includes(id)),
	};
}
